import Spinner from "@/Components/Spinner/Spinner";
import {
  PanelHeader,
  PanelState,
} from "@/Components/WebsiteSettings/SettingsPanel";
import useFranchiseHook from "@/shared/hooks/useFranchiseHook";
import useWebsiteSettingsHook from "@/shared/hooks/useWebsiteSettingsHook";
import { getCloudinaryFileId, isAiImage } from "@/shared/imageLibrary/aiImage";
import { supportedWebsiteLanguages } from "@/shared/utils/constant";
import { AccountRequirementEnum } from "@/shared/utils/enum";
import { asArray, getFormData } from "@/shared/utils/helpers";
import { ReactNode, useEffect, useRef } from "react";

export const imageType = {
  SLIDER_IMAGE: "slider-image",
  LOGO: "logo",
  NAV_IMAGE: "nav-image",
  MENU_HEADER: "menu_header",
  SLIDER: "slider",
};

export const DEFAULT_IMAGE_ID = "transpatentEmpty_sr5jbs";

export type TImageType =
  "slider-image" | "logo" | "nav-image" | "about-us-image";

export const deliveryMethodsEnum = {
  1: "delivery",
  2: "takeAway",
  3: "dineIn",
};

const defaultSectionList = [
  { label: "reviews", value: 1, name: "recencies", isActive: false },
  { label: "contact", value: 4, name: "contact", isActive: false },
  { label: "appBanner", value: 5, name: "appBanner", isActive: false },
];

export const getLanguages = (languages: string | string[]) => {
  if (!languages || parseFloat(String(languages)) === 0) {
    return supportedWebsiteLanguages;
  }
  const selectedIds = asArray(languages).map(Number);
  const selected = selectedIds
    .map((id) => supportedWebsiteLanguages.find((l) => l.value === id))
    .filter(Boolean)
    .map((l) => ({ ...l, isActive: true }));
  const rest = supportedWebsiteLanguages
    .filter((l) => !selectedIds.includes(l.value))
    .map((l) => ({ ...l, isActive: false }));
  return [...selected, ...rest];
};

export const getSectionList = (sections: string | string[]) => {
  const selectedIds = asArray(sections).map(Number);
  const selected = selectedIds
    .map((id) => defaultSectionList.find((item) => item.value === id))
    .filter(Boolean)
    .map((item) => ({ ...item, isActive: true }));
  const rest = defaultSectionList.filter(
    (item) => !selectedIds.includes(item.value)
  );
  return [...selected, ...rest];
};

/** Stored image ids, the way the settings endpoint expects them back. */
const getStoredImages = (images: string | string[]) =>
  asArray(images)
    .filter((image) => image && image !== DEFAULT_IMAGE_ID)
    .map((image) => (isAiImage(image) ? getCloudinaryFileId(image) : image));

/**
 * The settings endpoint is a full PUT, while each settings page only edits
 * part of it. This builds the complete payload from the stored settings, so a
 * page can overwrite just its own fields.
 */
const getStoredPayload = (data: any) => {
  const sliders = getStoredImages(data.home_sliders);
  return {
    languages: getLanguages(data.languages)
      .filter((l) => l.isActive)
      .map((l) => l.value),
    logo: getStoredImages(data.logo)[0] ?? DEFAULT_IMAGE_ID,
    home_sliders: sliders.length ? sliders : [DEFAULT_IMAGE_ID],
    header_background_image:
      getStoredImages(data.header_background_image)[0] ?? DEFAULT_IMAGE_ID,
    sections: getSectionList(data.sections)
      .filter((i) => i.isActive)
      .map((i) => i.value),
    payment_method_position: asArray(data.payment_method_position),
    delivery_method_position: asArray(data.delivery_method_position),
    color_template_id: data.color_template_id
      ? Number(data.color_template_id)
      : data.color_template_id,
    is_18_plus_website: data.is_18_plus_website ?? false,
    account_requirement:
      data.account_requirement ?? AccountRequirementEnum.NO_REQUIREMENT,
    social_media_urls: ((data.social_media_urls ?? []) as string[]).map(
      (url) => url ?? ""
    ),
    about_us_content: data.about_us_content ?? "",
  };
};

export type WebsiteSettingsPayload = ReturnType<typeof getStoredPayload>;

/** Saves part of the website settings and uploads their images. */
export const useWebsiteSettingsSave = (websiteSettingsData: any) => {
  const [franchiseId] = useFranchiseHook();
  const {
    postImageToCloudinary,
    postWebsiteSettings,
    uploadImageToCloudinary,
  } = useWebsiteSettingsHook({ franchiseId });
  const payload = useRef<WebsiteSettingsPayload>(null);
  if (!payload.current) {
    payload.current = getStoredPayload(websiteSettingsData);
  }

  const save = async (changes: Partial<WebsiteSettingsPayload>) => {
    const data = { ...payload.current, ...changes };
    await postWebsiteSettings.mutateAsync({
      data,
      restaurantSiteSettingsID: websiteSettingsData.item_id,
    });
    payload.current = data;
  };

  const postImage = async (image: File | string, type: TImageType) => {
    if (image instanceof File) {
      const signData = await postImageToCloudinary.mutateAsync(type);
      const formData = getFormData(image, signData);
      const { data } = await uploadImageToCloudinary.mutateAsync({
        url: signData.upload_url,
        formData,
      });
      return `v${data?.version}/${
        data?.secure_url?.split("/")?.reverse()?.[0]
      }`;
    } else if (isAiImage(image)) {
      // AI images from the image library are linked by their file id.
      return getCloudinaryFileId(image);
    } else {
      const [fileId, , , , , version] = image.split("/").reverse();
      return `${version}/${fileId}`;
    }
  };

  const isSaving =
    postWebsiteSettings.isPending ||
    postImageToCloudinary.isPending ||
    uploadImageToCloudinary.isPending;

  return { save, postImage, isSaving };
};

interface WebsiteSettingsLoaderProps {
  /** Shows a spinner until this is done as well. */
  isPending?: boolean;
  children: (websiteSettingsData: any) => ReactNode;
}

/** Loads the website settings of the current franchise for a settings page. */
export const WebsiteSettingsLoader = ({
  isPending,
  children,
}: WebsiteSettingsLoaderProps) => {
  const [franchiseId] = useFranchiseHook();
  const { websiteSettingsData, websiteSettingsQuery } = useWebsiteSettingsHook({
    franchiseId,
  });

  useEffect(() => {
    websiteSettingsQuery.refetch();
  }, [franchiseId]);

  if (websiteSettingsQuery.isFetching || isPending) {
    return (
      <>
        <PanelHeader />
        <PanelState>
          <Spinner size="0.8rem" />
        </PanelState>
      </>
    );
  }

  if (websiteSettingsQuery.isError) {
    return (
      <>
        <PanelHeader />
        <PanelState>error</PanelState>
      </>
    );
  }

  if (!websiteSettingsQuery.isSuccess || !websiteSettingsData) return null;

  return <>{children(websiteSettingsData)}</>;
};
